import { http } from '../../http';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';
import { __ } from '@/js/stores/expressions';

export type Category = {
    id: number;
    name: string;
    isActive: boolean;
    prompt: string;
    externalId: string | null;
    parentId: number | null;
    parentName: string | null;
};

// No externalId here - it's only ever assigned by the external-system ingestion path, never
// by BackOffice, so the create payload can't set it.
export type CreateCategoryInput = {
    name: string;
    isActive: boolean;
    prompt: string;
    parentId: number | null;
};

// No parentId - a category's parent is fixed at creation and can never change (interactions
// are categorized with a main/subcategory pair, so reparenting would rewrite past meaning).
export type UpdateCategoryInput = {
    name: string;
    isActive: boolean;
    prompt: string;
};

export const getCategories = async (tenantId: string): Promise<Category[]> => {
    return http
        .backoffice()
        .get<Category[]>(
            `categories/${tenantId}`,
            useCustomConfig({ errorMessage: { 500: __('back-office-categories') } })
        )
        .then((response) => response.data);
};

export const createCategory = async (tenantId: string, data: CreateCategoryInput): Promise<Category> => {
    return http
        .backoffice()
        .post<Category>(
            `categories/${tenantId}`,
            data,
            // silent: the mutation's onError shows the backend's actual `detail` message
            // instead of this interceptor's generic per-status-code toast.
            useCustomConfig({ silent: true })
        )
        .then((response) => response.data);
};

export const updateCategory = async (
    tenantId: string,
    id: number,
    data: UpdateCategoryInput
): Promise<Category> => {
    return http
        .backoffice()
        .put<Category>(`categories/${tenantId}/${id}`, data, useCustomConfig({ silent: true }))
        .then((response) => response.data);
};

// silent: the caller inspects the response itself - a 409 means the category is still linked
// to transcriptions and the caller re-asks with confirm=true, so a generic error toast here
// would be misleading.
export const deleteCategory = async (
    tenantId: string,
    id: number,
    confirm: boolean
): Promise<void> => {
    return http
        .backoffice()
        .delete(
            `categories/${tenantId}/${id}`,
            useCustomConfig({ silent: true, params: { confirm } })
        )
        .then(() => undefined);
};
